import type {TriggerIntegrationId} from '@cohort/shared/schema/apps';
import type {
  TriggerIntegrationSpec,
  TriggerIntegrationStruct,
} from '@cohort/shared/schema/apps/app';
import type {App} from '@cohort/wallet/apps';
import {Apps} from '@cohort/wallet/apps';
import type {ActionCtaTrakingMetadata} from '@cohort/wallet/components/space/ActionCtaComponentWrapper';
import type {JourneyStepTriggerWDto} from '@cohort/wallet-schemas/journeySteps';
import type {VerificationAttemptInput} from '@cohort/wallet-schemas/journeyStepTriggers';

export type DetailsComponentProps<T extends TriggerIntegrationStruct = TriggerIntegrationStruct> = {
  config: T['Config'];
};

export type ActionCtaComponentProps<T extends TriggerIntegrationStruct = TriggerIntegrationStruct> =
  {
    config: T['Config'];
    processStepVerification: () => void;
    trackingData: ActionCtaTrakingMetadata;
    triggerId: string;
    journeyId: string;
    stepId: string;
  };

export type TriggerIntegrationUsageComponentProps<
  T extends TriggerIntegrationStruct = TriggerIntegrationStruct,
> = {
  onTriggerIntegrationUsageSuccess: (data: VerificationAttemptInput) => void;
  config: T['Config'];
};

export type TriggerIntegration<T extends TriggerIntegrationStruct = TriggerIntegrationStruct> = {
  actionCtaComponent: React.FC<ActionCtaComponentProps<T>> | null;
  detailsComponent: React.FC<DetailsComponentProps<T>> | null;
  spec: TriggerIntegrationSpec<T>;
  usageComponent: React.FC<TriggerIntegrationUsageComponentProps<T>> | null;
};

export const getAppsWithTriggerIntegrations = (): App[] => {
  return Apps.filter(app => app.spec.triggerIntegrationSpecs.length > 0);
};

export const getAppFromTriggerIntegration = (triggerIntegrationId: TriggerIntegrationId): App => {
  const appsListWithTriggers = getAppsWithTriggerIntegrations();
  const app = appsListWithTriggers.find(app =>
    app.triggerIntegrations.find(integration => integration.spec.id === triggerIntegrationId)
  );

  if (!app) {
    throw new Error(`No app found for trigger ${triggerIntegrationId}`);
  }
  return app;
};

export const getDetailsComponentFromTriggerIntegration = (
  trigger: JourneyStepTriggerWDto
): React.FC<DetailsComponentProps> | null => {
  const app = getAppFromTriggerIntegration(trigger.triggerIntegrationId);
  const DetailsComponent = app.triggerIntegrations.find(
    integration => integration.spec.id === trigger.triggerIntegrationId
  )?.detailsComponent;

  if (!DetailsComponent) {
    return null;
  }

  return DetailsComponent;
};

export const getActionCtaComponentFromTriggerIntegration = (
  triggerIntegrationId: TriggerIntegrationId
): React.FC<ActionCtaComponentProps> | null => {
  const app = getAppFromTriggerIntegration(triggerIntegrationId);
  const ActionCtaComponent = app.triggerIntegrations.find(
    integration => integration.spec.id === triggerIntegrationId
  )?.actionCtaComponent;

  if (!ActionCtaComponent) {
    return null;
  }

  return ActionCtaComponent;
};
